import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock } from "lucide-react";
import { imgSrc } from "../lib/api";
import { formatTime } from "../constants/rox";
import { useT, useLabels } from "../i18n";

export const PairCard = ({ pair }) => {
  const { t } = useT();
  const L = useLabels();
  return (
  <article
    data-testid={`pair-card-${pair.id}`}
    className="panel relative overflow-hidden p-4 transition-[border-color] duration-200 hover:border-rox-off/40"
  >
    <div className="flex gap-4">
      <div className="relative h-28 w-24 shrink-0 overflow-hidden rounded-[4px] bg-rox-gray">
        {pair.photo_url ? (
          <img src={imgSrc(pair.photo_url)} alt={pair.first_name} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <span className="flex h-full w-full items-center justify-center font-display text-3xl uppercase text-rox-off/70">{pair.first_name.slice(0, 2)}</span>
        )}
        <span
          data-testid={`pair-score-${pair.id}`}
          className="absolute left-0 top-0 bg-rox-orange px-2 py-1 font-display text-sm uppercase tracking-wide text-rox-black"
        >
          {pair.score}%
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="h-display text-2xl">{pair.first_name}{pair.age ? <span className="text-rox-off/70">, {pair.age}</span> : null}</h3>
          {pair.matched && (
            <span className="flex items-center gap-1 rounded-sm border border-rox-orange px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rox-orange">
              <Check size={12} /> {t("match_badge")}
            </span>
          )}
          {!pair.matched && pair.interest_status && (
            <span data-testid={`pair-interest-badge-${pair.id}`} className="flex items-center gap-1 rounded-sm border border-rox-line px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rox-muted">
              <Clock size={12} /> {t("sent_badge")}
            </span>
          )}
        </div>
        <p className="text-sm text-rox-muted">
          {pair.city}
          {pair.distance_km ? ` · ${pair.distance_km} km` : ""}
          <span className="text-rox-orange"> · {pair.score}% {t("compatible")}</span>
        </p>
        <p className="mt-1.5 text-sm font-semibold">{pair.race_name}</p>
        <p className="text-xs text-rox-off/75">{L.category(pair)}</p>
      </div>
    </div>
    <dl className="mt-4 grid grid-cols-3 gap-2">
      {[[t("level"), L.level(pair)], [t("chrono"), formatTime(pair.target_time)], [t("goal"), L.goalShort(pair)]].map(([k, v]) => (
        <div key={k} className="rounded-[3px] bg-rox-black px-2.5 py-2">
          <dt className="eyebrow text-[10px]">{k}</dt>
          <dd className="mt-0.5 break-words text-xs font-semibold leading-tight" title={v}>{v}</dd>
        </div>
      ))}
    </dl>
    <p className="mt-3 text-sm text-rox-off" data-testid={`pair-strengths-${pair.id}`}>
      <span className="text-rox-orange">■</span> {L.strengths(pair)}
    </p>
    {pair.reasons?.length > 0 && (
      <p className="mt-1 text-xs text-rox-muted" data-testid={`pair-reason-${pair.id}`}>
        {L.reasons(pair).slice(0, 2).join(" · ")}
      </p>
    )}
    <Link
      to={`/athlete/${pair.id}`}
      data-testid={`view-profile-${pair.id}`}
      className="mt-4 flex items-center justify-between rounded-[4px] border border-rox-line px-4 py-3 font-display text-sm uppercase tracking-wider transition-[border-color,color] hover:border-rox-orange hover:text-rox-orange"
    >
      {t("view_profile")} <ArrowRight size={16} />
    </Link>
  </article>
  );
};
